import React, { useState, useEffect } from 'react';
import { Box, Typography, Alert, Stack, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { toast } from 'react-toastify';
import LoadingSpinner from '@components/LoadingSpinner';
import OnboardingHeader from '../components/onboarding/OnboardingHeader';
import { updateBusinessStatus, updateStaffStatus } from '../services/profileService';
import type { User as SupabaseUser } from '@supabase/supabase-js';

interface ListingStatus {
  stylistListed: boolean;
  businessListed: boolean;
  isProcessing: boolean;
  completed: boolean;
}

interface StoredUser {
  _id?: string;
  [key: string]: unknown;
}

interface ListingError extends Error {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
}

const QuickEmailVerificationSuccess: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language || 'zh');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userData, setUserData] = useState<SupabaseUser | null>(null);
  const [listingStatus, setListingStatus] = useState<ListingStatus>({
    stylistListed: false,
    businessListed: false,
    isProcessing: false,
    completed: false
  });

  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase.auth.getUser();
        if (error) throw error;
        setUserData(data.user);
      } catch (error: unknown) {
        console.error('Error checking auth status:', error);
        toast.error('Authentication error. Please login again.');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  // Auto-start listing process after authentication is confirmed
  useEffect(() => {
    if (userData && !listingStatus.isProcessing && !listingStatus.completed) {
      console.log('Starting listing process for user:', userData);
      startListingProcess();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]); // Only depend on userData to avoid infinite loops

  const startListingProcess = async (): Promise<void> => {
    // Prevent multiple executions
    if (listingStatus.isProcessing || listingStatus.completed) {
      console.log('Listing process already running or completed');
      return;
    }

    setListingStatus(prev => ({ ...prev, isProcessing: true }));

    try {
      // Step 1: List stylist first
      console.log('Starting stylist listing...');
      await listStylist();

      // Step 2: List business after stylist is successful
      console.log('Starting business listing...');
      await listBusiness();

      setListingStatus(prev => ({
        ...prev,
        isProcessing: false,
        completed: true
      }));

      toast.success(t('quickOnboarding.success.listingComplete', 'Your stylist and business are now listed on HKStylist!'));
    } catch (error: unknown) {
      console.error('Error in listing process:', error);
      setListingStatus(prev => ({ ...prev, isProcessing: false }));

      const listingError = error as ListingError;
      // More specific error messages
      if (listingError.message?.includes('Staff ID not found')) {
        toast.error('User information not available. Please try logging in again.');
      } else if (listingError.response?.status === 404) {
        toast.error('Service not available. Please contact support.');
      } else {
        toast.error(t('quickOnboarding.success.listingUpdateError', 'Failed to update listing status. Please contact support.'));
      }
    }
  };

  const listStylist = async (): Promise<void> => {
    try {
      if (!userData || !userData.email) {
        throw new Error('User data not available');
      }

      // Get the current user from localStorage to find staff details
      const storedUser: StoredUser = JSON.parse(localStorage.getItem('user') || '{}');
      const staffId = storedUser._id || userData.id;

      if (!staffId) {
        throw new Error('Staff ID not found');
      }

      console.log('Listing stylist with staffId:', staffId);
      const response = await updateStaffStatus({ staffId, status: 'listing' });

      if (response) {
        setListingStatus(prev => ({ ...prev, stylistListed: true }));
        console.log('Stylist listed successfully');
      } else {
        throw new Error('Failed to list stylist');
      }
    } catch (error: unknown) {
      console.error('Error listing stylist:', error);
      throw error;
    }
  };

  const listBusiness = async (): Promise<void> => {
    try {
      console.log('Listing business...');
      const response = await updateBusinessStatus({ status: 'listing' });

      if (response) {
        setListingStatus(prev => ({ ...prev, businessListed: true }));
        console.log('Business listed successfully');
      } else {
        throw new Error('Failed to list business');
      }
    } catch (error: unknown) {
      console.error('Error listing business:', error);
      throw error;
    }
  };

  const handleLanguageChange = (lang: string): void => {
    setCurrentLanguage(lang);
    i18n.changeLanguage(lang);
  };

  const handleGoToDashboard = (): void => {
    navigate('/dashboard');
  };

  if (isLoading && !userData) {
    return <LoadingSpinner />;
  }

  return (
    <Box className="onboarding-container">
      <OnboardingHeader
        title={t('quickOnboarding.success.title', 'Quick Email Verification Success')}
        currentLanguage={currentLanguage}
        changeLanguage={handleLanguageChange}
      />

      <Stack direction="column" spacing={3} sx={{ maxWidth: 600, mx: 'auto', padding: 4 }}>
        <Box className="main">
          {/* Email Verification Success */}
          <Typography variant="h4" gutterBottom align="center" sx={{ color: '#C7A46E', mb: 4 }}>
            🎉 {t('quickOnboarding.success.emailVerified', 'Email Verified Successfully!')}
          </Typography>

          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1">
              {t('quickOnboarding.success.emailVerificationComplete', 'Your email has been verified successfully! We are now setting up your profile to go live on HKStylist.')}
            </Typography>
          </Alert>

          {/* Complete Setup Call-to-Action */}
          {(
            <>
              <Box sx={{
                mb: 3,
                bgcolor: '#e8f5e8',
                border: '2px solid #4caf50',
                borderRadius: 2,
                p: 3,
                background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)'
              }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  🎯 {t('quickOnboarding.success.almostDone', 'You can still unlock reservation functionality!')}
                </Typography>
                <Typography variant="body1" sx={{ color: '#2e7d32', mb: 2 }}>
                  {t('quickOnboarding.success.completeSetup', 'Complete your quick setup wizard to unlock reservation functionality and start accepting bookings.')}
                </Typography>
                <Typography variant="body1" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  📈 {t('quickOnboarding.success.bookingIncrease', 'Stylists who complete the full setup receive 70% more bookings!')}
                </Typography>
              </Box>

              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 2, color: '#666' }}>
                  {t('quickOnboarding.success.dashboardAccess', 'Access your dashboard to complete the remaining setup steps and start growing your business.')}
                </Typography>
              </Box>

              <Button
                variant="contained"
                color="primary"
                onClick={handleGoToDashboard}
                fullWidth
                size="large"
                sx={{
                  py: 2,
                  fontSize: '1.1rem',
                  background: 'linear-gradient(45deg, #C7A46E 30%, #D4B896 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #B8956A 30%, #C7A46E 90%)',
                  }
                }}
              >
                {t('quickOnboarding.success.completeDashboard', 'Complete Setup in Dashboard')} →
              </Button>
            </>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default QuickEmailVerificationSuccess;
